import { jsx, jsxs } from 'react/jsx-runtime';
import { createContext, useMemo, useContext, useRef, useState, useCallback, useEffect } from 'react';

/**
 * Default theme for toast notifications
 * These match the original Tailwind colors
 */
/**
 * Default toast background gradients
 */
const defaultToastTheme = {
    success: {
        from: 'rgb(168, 85, 247)', // purple-500
        to: 'rgb(99, 102, 241)', // indigo-500
        opacity: 0.9,
    },
    error: {
        from: 'rgb(168, 85, 247)', // purple-500
        to: 'rgb(244, 63, 94)', // rose-500
        opacity: 0.9,
    },
    warning: {
        from: 'rgb(249, 115, 22)', // orange-500
        to: 'rgb(168, 85, 247)', // purple-500
        opacity: 0.9,
    },
    info: {
        from: 'rgb(59, 130, 246)', // blue-500
        to: 'rgb(168, 85, 247)', // purple-500
        opacity: 0.9,
    },
};
/**
 * Default progress bar gradients
 */
const defaultProgressBarTheme = {
    success: {
        from: 'rgb(192, 132, 252)', // purple-400
        to: 'rgb(129, 140, 248)', // indigo-400
    },
    error: {
        from: 'rgb(192, 132, 252)', // purple-400
        to: 'rgb(251, 113, 133)', // rose-400
    },
    warning: {
        from: 'rgb(251, 146, 60)', // orange-400
        to: 'rgb(192, 132, 252)', // purple-400
    },
    info: {
        from: 'rgb(96, 165, 250)', // blue-400
        to: 'rgb(192, 132, 252)', // purple-400
    },
};
/**
 * Complete default theme with all values resolved
 */
const defaultTheme = {
    toast: defaultToastTheme,
    progressBar: defaultProgressBarTheme,
};

const ThemeContext = createContext({
    theme: defaultTheme,
});
/**
 * Hook to access the current theme
 */
const useTheme = () => useContext(ThemeContext);
/**
 * Theme provider for customizing toast gradients
 *
 * @example
 * ```tsx
 * const customTheme = {
 *   toast: {
 *     success: {
 *       from: '#10b981',
 *       to: '#3b82f6',
 *       opacity: 0.95,
 *     },
 *   },
 * };
 *
 * <ThemeProvider theme={customTheme}>
 *   <App />
 * </ThemeProvider>
 * ```
 */
const ThemeProvider = ({ theme: userTheme = {}, children }) => {
    const resolvedTheme = useMemo(() => {
        return {
            toast: {
                success: {
                    from: userTheme.toast?.success?.from ?? defaultTheme.toast.success.from,
                    to: userTheme.toast?.success?.to ?? defaultTheme.toast.success.to,
                    opacity: userTheme.toast?.success?.opacity ?? defaultTheme.toast.success.opacity,
                },
                error: {
                    from: userTheme.toast?.error?.from ?? defaultTheme.toast.error.from,
                    to: userTheme.toast?.error?.to ?? defaultTheme.toast.error.to,
                    opacity: userTheme.toast?.error?.opacity ?? defaultTheme.toast.error.opacity,
                },
                warning: {
                    from: userTheme.toast?.warning?.from ?? defaultTheme.toast.warning.from,
                    to: userTheme.toast?.warning?.to ?? defaultTheme.toast.warning.to,
                    opacity: userTheme.toast?.warning?.opacity ?? defaultTheme.toast.warning.opacity,
                },
                info: {
                    from: userTheme.toast?.info?.from ?? defaultTheme.toast.info.from,
                    to: userTheme.toast?.info?.to ?? defaultTheme.toast.info.to,
                    opacity: userTheme.toast?.info?.opacity ?? defaultTheme.toast.info.opacity,
                },
            },
            progressBar: {
                success: {
                    from: userTheme.progressBar?.success?.from ?? defaultTheme.progressBar.success.from,
                    to: userTheme.progressBar?.success?.to ?? defaultTheme.progressBar.success.to,
                },
                error: {
                    from: userTheme.progressBar?.error?.from ?? defaultTheme.progressBar.error.from,
                    to: userTheme.progressBar?.error?.to ?? defaultTheme.progressBar.error.to,
                },
                warning: {
                    from: userTheme.progressBar?.warning?.from ?? defaultTheme.progressBar.warning.from,
                    to: userTheme.progressBar?.warning?.to ?? defaultTheme.progressBar.warning.to,
                },
                info: {
                    from: userTheme.progressBar?.info?.from ?? defaultTheme.progressBar.info.from,
                    to: userTheme.progressBar?.info?.to ?? defaultTheme.progressBar.info.to,
                },
            },
        };
    }, [userTheme]);
    return (jsx(ThemeContext.Provider, { value: { theme: resolvedTheme }, children: children }));
};

/**
 * Style helper functions for toast components
 * Converts Tailwind classes to inline React styles
 */
/**
 * Hook to get background gradient style for toast
 * Uses theme context for customizable colors
 */
const useToastBackgroundStyle = (type, override) => {
    const { theme } = useTheme();
    const gradient = theme.toast[type];
    return {
        background: `linear-gradient(to right, ${gradient.from}, ${gradient.to})`,
        opacity: gradient.opacity ?? 0.9,
    };
};
/**
 * Hook to get progress bar gradient style
 * Uses theme context for customizable colors
 */
const useProgressBarStyle = (type, progress) => {
    const { theme } = useTheme();
    const gradient = theme.progressBar[type];
    return {
        width: `${progress}%`,
        background: `linear-gradient(to right, ${gradient.from}, ${gradient.to})`,
        height: '4px',
        transition: 'width 100ms linear',
    };
};

const Toast = ({ toast, isExiting, onRemove, onMakePermanent }) => {
    const removeTimeoutRef = useRef(null);
    const [progress, setProgress] = useState(toast.progress || 0);
    const [isPaused, setIsPaused] = useState(false);
    const [swipeOffset, setSwipeOffset] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [_mouseDownTime, setMouseDownTime] = useState(0);
    const [_hasMoved, setHasMoved] = useState(false);
    const handleRemove = useCallback(() => {
        if (onRemove) {
            onRemove();
        }
    }, [onRemove]);
    const handleMouseEnter = useCallback(() => {
        setIsPaused(true);
    }, []);
    const handleMouseLeave = useCallback(() => {
        setIsPaused(false);
    }, []);
    // Swipe handlers
    const handleTouchStart = useCallback((e) => {
        const touch = e.touches[0];
        setStartX(touch.clientX);
        setIsDragging(true);
        setIsPaused(true);
    }, []);
    const handleTouchMove = useCallback((e) => {
        if (!isDragging)
            return;
        const touch = e.touches[0];
        const currentX = touch.clientX;
        const deltaX = currentX - startX;
        if (deltaX > 0) {
            setSwipeOffset(deltaX);
        }
    }, [isDragging, startX]);
    const handleTouchEnd = useCallback(() => {
        if (!isDragging)
            return;
        setIsDragging(false);
        setIsPaused(false);
        if (swipeOffset > 100) {
            handleRemove();
        }
        else {
            setSwipeOffset(0);
        }
    }, [isDragging, swipeOffset, handleRemove]);
    // Mouse drag handlers for desktop
    const handleMouseDown = useCallback((e) => {
        setStartX(e.clientX);
        setMouseDownTime(Date.now());
        setHasMoved(false);
        setIsDragging(false);
        // Immediately pause on mouse down (click to pause)
        if (!toast.isPermanent) {
            setIsPaused(true);
        }
    }, [toast.isPermanent]);
    const handleMouseMove = useCallback((e) => {
        if (e.buttons !== 1)
            return; // Only if left mouse button is pressed
        const currentX = e.clientX;
        const deltaX = currentX - startX;
        // Mark that mouse has moved
        if (Math.abs(deltaX) > 2) {
            setHasMoved(true);
        }
        // Only start dragging if moved more than 5px
        if (Math.abs(deltaX) > 5 && !isDragging) {
            setIsDragging(true);
            setIsPaused(true);
        }
        if (isDragging && deltaX > 0) {
            setSwipeOffset(deltaX);
        }
    }, [isDragging, startX]);
    const handleMouseUp = useCallback((_e) => {
        if (isDragging) {
            setIsDragging(false);
            setIsPaused(false);
            if (swipeOffset > 100) {
                handleRemove();
            }
            else {
                setSwipeOffset(0);
            }
        }
        // Reset tracking variables
        setHasMoved(false);
        setMouseDownTime(0);
    }, [isDragging, swipeOffset, handleRemove]);
    useEffect(() => {
        if (toast.progress !== undefined) {
            setProgress(toast.progress);
        }
    }, [toast.progress]);
    useEffect(() => {
        if (toast.isPermanent || isPaused || toast.progress !== undefined)
            return;
        const duration = toast.duration || 10000;
        const interval = setInterval(() => {
            setProgress((prev) => {
                const newProgress = prev + 100 / (duration / 100);
                if (newProgress >= 100) {
                    clearInterval(interval);
                    // Defer handleRemove to next tick to avoid setState during render warning
                    setTimeout(() => handleRemove(), 0);
                    return 100;
                }
                return newProgress;
            });
        }, 100);
        return () => clearInterval(interval);
    }, [toast.duration, toast.isPermanent, isPaused, handleRemove, toast.progress]);
    useEffect(() => {
        return () => {
            if (removeTimeoutRef.current) {
                clearTimeout(removeTimeoutRef.current);
            }
        };
    }, []);
    const [isHovered, setIsHovered] = useState(false);
    const handleMouseEnterWrapper = useCallback(() => {
        setIsHovered(true);
        handleMouseEnter();
    }, [handleMouseEnter]);
    const handleMouseLeaveWrapper = useCallback(() => {
        setIsHovered(false);
        handleMouseLeave();
    }, [handleMouseLeave]);
    const backgroundStyle = useToastBackgroundStyle(toast.type);
    const progressStyle = useProgressBarStyle(toast.type, progress);
    return (jsx("div", { style: {
            transform: isExiting ? 'translateX(100%) scale(0.95)' : 'translateX(0) scale(1)',
            opacity: isExiting ? 0 : 1,
            transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        }, children: jsxs("div", { style: {
                ...backgroundStyle,
                backdropFilter: 'blur(4px)',
                borderRadius: '0.5rem',
                padding: '1rem',
                boxShadow: isHovered
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                cursor: toast.isPermanent ? 'default' : isDragging ? 'grabbing' : 'grab',
                transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                transform: `translateX(${swipeOffset}px) scale(${isHovered ? 1.05 : 1})`,
                opacity: Math.max(0.3, 1 - swipeOffset / 200),
                width: '100%',
                position: 'relative',
                overflow: 'hidden',
            }, onMouseEnter: handleMouseEnterWrapper, onMouseLeave: handleMouseLeaveWrapper, onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd, onMouseDown: handleMouseDown, onMouseMove: handleMouseMove, onMouseUp: handleMouseUp, role: "alert", "aria-live": "polite", children: [jsxs("div", { style: { display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }, children: [jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [toast.title && (jsx("p", { style: {
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        color: '#ffffff',
                                        marginBottom: '0.25rem',
                                    }, children: toast.title })), jsx("p", { style: {
                                        fontSize: '0.875rem',
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        lineHeight: 1.625,
                                    }, children: toast.message })] }), jsx("button", { onClick: (e) => {
                                e.stopPropagation();
                                handleRemove();
                            }, style: {
                                flexShrink: 0,
                                padding: '0.25rem',
                                backgroundColor: isHovered ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                                borderRadius: '9999px',
                                transition: 'background-color 200ms',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }, "aria-label": "Dismiss notification", children: jsx("svg", { style: { width: '1rem', height: '1rem', color: '#ffffff' }, fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) }) })] }), jsx("div", { style: {
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    }, children: jsx("div", { style: progressStyle }) })] }) }));
};

const ToastContainer = ({ toasts, onRemoveToast, isDesktop = true, }) => {
    const [animatedToasts, setAnimatedToasts] = useState([]);
    const toastRefs = useRef(new Map());
    // Calculate Y positions for all toasts (excluding exiting toasts)
    const calculatePositions = useCallback((toastList) => {
        let currentY = 0;
        return toastList.map((toast) => {
            const element = toastRefs.current.get(toast.id);
            const height = element ? element.offsetHeight : 80; // fallback height
            // Only increment position for non-exiting toasts
            const yPosition = toast.isExiting ? toast.yPosition || 0 : currentY;
            if (!toast.isExiting) {
                currentY += height + 12; // 12px gap
            }
            return {
                ...toast,
                yPosition,
                height,
            };
        });
    }, []);
    // Sync with toasts and manage enter/exit animations
    useEffect(() => {
        setAnimatedToasts((current) => {
            const currentIds = new Set(current.map((t) => t.id));
            // Add new toasts with enter animation
            const newToasts = toasts
                .filter((t) => !currentIds.has(t.id))
                .map((t) => ({
                ...t,
                isEntering: true,
                isExiting: false,
                animationKey: `${t.id}-${Date.now()}`,
                yPosition: 0,
                height: 80,
            }));
            // Mark toasts for exit that are no longer in toasts, and update existing ones
            const updatedToasts = current.map((toast) => {
                const reduxToast = toasts.find((t) => t.id === toast.id);
                if (!reduxToast && !toast.isExiting) {
                    return { ...toast, isExiting: true, isEntering: false };
                }
                if (reduxToast) {
                    return { ...toast, ...reduxToast };
                }
                return toast;
            });
            // Calculate new positions for all toasts
            const allToasts = [...updatedToasts, ...newToasts];
            return calculatePositions(allToasts);
        });
    }, [toasts, calculatePositions]);
    // Update positions when toast heights change
    useEffect(() => {
        const timer = setTimeout(() => {
            setAnimatedToasts((prev) => {
                // Measure actual heights and recalculate positions
                const measuredToasts = prev.map((toast) => {
                    const element = toastRefs.current.get(toast.id);
                    const height = element ? element.offsetHeight : toast.height || 80;
                    return { ...toast, height };
                });
                return calculatePositions(measuredToasts.map((toast) => toast.isEntering ? { ...toast, isEntering: false } : toast));
            });
        }, 100); // Give time for DOM to update
        return () => clearTimeout(timer);
    }, [animatedToasts.filter((t) => t.isEntering).length, calculatePositions]);
    // Remove toasts after exit animation completes
    const handleToastExitComplete = useCallback((toastId) => {
        setAnimatedToasts((prev) => {
            const filtered = prev.filter((t) => t.id !== toastId);
            toastRefs.current.delete(toastId);
            return calculatePositions(filtered);
        });
    }, [calculatePositions]);
    useEffect(() => {
        const exitingToasts = animatedToasts.filter((t) => t.isExiting);
        if (exitingToasts.length > 0) {
            const timer = setTimeout(() => {
                exitingToasts.forEach((toast) => handleToastExitComplete(toast.id));
            }, 300); // Corresponds to the animation duration
            return () => clearTimeout(timer);
        }
    }, [animatedToasts, handleToastExitComplete]);
    // Removed aggressive click-outside handler that was clearing all toasts
    // when clicking buttons to create new toasts. This was causing the stacking bug.
    // Users can still dismiss individual toasts by swiping or clicking the X button.
    if (!animatedToasts || animatedToasts.length === 0) {
        return null;
    }
    const containerStyle = {
        position: "fixed",
        bottom: "1rem",
        right: "1rem",
        zIndex: 50,
        width: isDesktop ? "33vw" : "calc(100% - 1rem)",
        left: isDesktop ? "auto" : "0.5rem",
        maxWidth: isDesktop ? "33vw" : "100%",
        pointerEvents: "none",
    };
    return (jsx("div", { "data-toast-container": true, style: containerStyle, "aria-live": "polite", "aria-label": "Notifications", children: jsx("div", { style: { position: 'relative', width: '100%', pointerEvents: "auto" }, children: animatedToasts.map((toast) => {
                const getTransform = () => {
                    const yTransform = `translateY(-${toast.yPosition || 0}px)`;
                    if (toast.isExiting) {
                        return `${yTransform} translateX(100%) scale(0.95)`;
                    }
                    else if (toast.isEntering) {
                        return `${yTransform} translateX(100%) scale(0.95)`;
                    }
                    else {
                        return `${yTransform} translateX(0) scale(1)`;
                    }
                };
                const getOpacity = () => {
                    if (toast.isExiting || toast.isEntering) {
                        return 0;
                    }
                    return 1;
                };
                return (jsx("div", { ref: (el) => {
                        if (el)
                            toastRefs.current.set(toast.id, el);
                        else
                            toastRefs.current.delete(toast.id);
                    }, style: {
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        transform: getTransform(),
                        opacity: getOpacity(),
                        transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                    }, children: jsx(Toast, { toast: toast, isExiting: toast.isExiting, onRemove: () => onRemoveToast(toast.id) }) }, toast.animationKey || toast.id));
            }) }) }));
};

const ToastContext = createContext(undefined);
const ToastProvider = ({ children, theme }) => {
    const [toasts, setToasts] = useState([]);
    const addToast = useCallback((options) => {
        const { id, type, title, message, duration = 10000, progress } = options;
        const toastId = id || `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        setToasts((prev) => {
            const existingIndex = prev.findIndex((t) => t.id === toastId);
            if (existingIndex === -1) {
                const toast = {
                    id: toastId,
                    type,
                    title,
                    message,
                    duration,
                    progress,
                    isPaused: false,
                    isPermanent: false,
                    createdAt: Date.now(),
                };
                return [...prev, toast];
            }
            return prev;
        });
    }, []);
    const updateToast = useCallback((id, updates) => {
        setToasts((prev) => prev.map((toast) => (toast.id === id ? { ...toast, ...updates } : toast)));
    }, []);
    const removeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);
    const pauseToast = useCallback((id) => {
        setToasts((prev) => prev.map((toast) => (toast.id === id ? { ...toast, isPaused: true } : toast)));
    }, []);
    const resumeToast = useCallback((id) => {
        setToasts((prev) => prev.map((toast) => (toast.id === id ? { ...toast, isPaused: false } : toast)));
    }, []);
    const makeToastPermanent = useCallback((id) => {
        setToasts((prev) => prev.map((toast) => toast.id === id ? { ...toast, isPermanent: true, isPaused: true } : toast));
    }, []);
    const clearAllToasts = useCallback(() => {
        setToasts([]);
    }, []);
    const value = useMemo(() => ({
        toasts,
        addToast,
        updateToast,
        removeToast,
        pauseToast,
        resumeToast,
        makeToastPermanent,
        clearAllToasts,
    }), [toasts, addToast, updateToast, removeToast, pauseToast, resumeToast, makeToastPermanent, clearAllToasts]);
    return (jsx(ThemeProvider, { theme: theme, children: jsx(ToastContext.Provider, { value: value, children: children }) }));
};
const useToastContext = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToastContext must be used within a ToastProvider');
    }
    return context;
};

/**
 * Hook to detect if a media query matches
 * @param query - CSS media query string (e.g., '(min-width: 640px)')
 * @returns boolean indicating if the query matches
 */
const useMediaQuery = (query) => {
    const [matches, setMatches] = useState(false);
    useEffect(() => {
        const media = window.matchMedia(query);
        // Set initial value
        if (media.matches !== matches) {
            setMatches(media.matches);
        }
        // Create listener for changes
        const listener = () => setMatches(media.matches);
        // Add listener (modern browsers)
        media.addEventListener('change', listener);
        // Cleanup
        return () => media.removeEventListener('change', listener);
    }, [matches, query]);
    return matches;
};

/**
 * Standalone toast container (no Redux required)
 * Uses React Context for state management
 * Must be used within a ToastProvider
 */
const StandaloneToastContainer = () => {
    const { toasts, removeToast } = useToastContext();
    const isDesktop = useMediaQuery('(min-width: 640px)');
    return (jsx(ToastContainer, { toasts: toasts, onRemoveToast: removeToast, isDesktop: isDesktop }));
};

/**
 * Standalone toast hook - uses React Context only
 * Must be used within a ToastProvider
 */
const useToast = () => {
    const { addToast } = useToastContext();
    const toast = useCallback((options) => {
        addToast(options);
    }, [addToast]);
    const success = useCallback((options) => {
        toast({ ...options, type: 'success' });
    }, [toast]);
    const error = useCallback((options) => {
        toast({ ...options, type: 'error' });
    }, [toast]);
    const warning = useCallback((options) => {
        toast({ ...options, type: 'warning' });
    }, [toast]);
    const info = useCallback((options) => {
        toast({ ...options, type: 'info' });
    }, [toast]);
    return {
        toast,
        success,
        error,
        warning,
        info,
    };
};

export { StandaloneToastContainer, Toast, ToastContainer, ToastProvider, useMediaQuery, useToast, useToastContext };
//# sourceMappingURL=standalone.esm.js.map
